import DatabaseModel from './DatabaseModel.js';

var CronTask = Object.create(DatabaseModel);

CronTask.model = 'core/schedulerTasks';
CronTask.modelFront = 'tareasprogramadas';

// La ficha de cualquier modelo pide el histórico al abrirse (`rForm.loadItem` llama a
// `hasHistory`), pero `/core/schedulerTasks` lo sirve `CronTaskController` de digitaltpm-core, que
// NO extiende `BaseController` —donde vive `/hasHistory`—: la petición cae en su
// `@GetMapping("/{id}")`, el servidor la toma por el id de una tarea y contesta 412
// `MODEL_NOT_FOUND` con «hasHistory» por mensaje. Una tarea programada no tiene histórico, así que
// se contesta aquí y no se pregunta: desaparece el aviso al abrir la ficha y el servidor deja de
// anotar un error por cada apertura. El arreglo de fondo es de core (que `CronTaskController`
// extienda `BaseController` o declare `/hasHistory`), y core no se toca desde aquí.
CronTask.hasHistory = function() {
    return Promise.resolve(false);
};

/**
 * El mapa de parámetros (`datos`) de una tarea, TERMINADO: todas las claves como propiedades
 * propias con el nombre exacto que se escribió, y cerrado para que nadie le añada ni le cambie
 * nada después.
 *
 * Es la única puerta por la que se construye ese mapa, de ida y de vuelta, porque un parámetro
 * desaparecía del documento por cómo se llamaba y **cada vez por un mecanismo distinto** (revisión
 * del PR #1790). El camino del mapa tiene dos piezas que reclaman un nombre para sí:
 *
 *  - la ASIGNACIÓN `mapa[clave] = valor` llama al accesor que la clave tenga heredado, y
 *    `Object.prototype` tiene uno: `__proto__`. No deja ninguna propiedad propia —si el valor es
 *    un objeto le cambia el prototipo al mapa, y si es un número o un texto no hace nada—, así que
 *    el parámetro no sale en `Object.keys` ni en el JSON del PUT. Se cierra definiendo la
 *    propiedad, que no pasa por ningún accesor, sea el nombre el que sea;
 *  - la REACTIVIDAD de Vue 2: al meter el mapa en `formData` (y al meter el documento del servidor
 *    en el `formData` de `rForm`), su `Observer` instala en el objeto una propiedad interna no
 *    enumerable llamada `__ob__`, pisando la que hubiera con ese nombre. La fila seguía viéndose en
 *    la pantalla, pero el parámetro ya no se serializaba y el guardado siguiente lo perdía. Se
 *    cierra con `Object.freeze`: Vue no hace reactivo un objeto congelado —no lo observa y por
 *    tanto no le instala nada—, que es justo el contrato que hace falta aquí, porque este mapa no
 *    se parchea nunca: se rehace entero en cada pulsación.
 *
 * Y son las dos únicas, porque las demás piezas del camino no se quedan con ningún nombre:
 * `Object.keys` y `JSON.stringify` solo miran propiedades propias enumerables, y el único nombre
 * que `JSON.stringify` trataría aparte —`toJSON`— tiene que valer una función, y aquí los valores
 * salen de un campo de texto o de `JSON.parse` y nunca lo son.
 *
 * **No hay ningún nombre prohibido, y no puede haberlo**: estos `datos` existen desde antes que la
 * pantalla, así que negar una clave que una tarea ya tiene guardada la haría ineditable —y
 * volvería a perderla, solo que por otro camino—.
 *
 * Toma las entradas como pares `[clave, valor]` y no un objeto ya montado a propósito: montarlo
 * para pasarlo por aquí sería volver a la asignación, y ya habría perdido la clave antes de entrar.
 */
CronTask.mapaDeParametros = function(entradas) {
    const mapa = {};
    (entradas || []).forEach(function(entrada) {
        Object.defineProperty(mapa, entrada[0], {
            value: entrada[1],
            enumerable: true,
            writable: true,
            configurable: true
        });
    });
    return Object.freeze(mapa);
};

/**
 * El documento que contesta el servidor, con su mapa de parámetros reconstruido por esa única
 * puerta ANTES de que lo vea nadie.
 *
 * Esta es la otra mitad del mismo olvido, que la revisión no señaló: `rForm` mete el documento en
 * su `formData` —reactivo— y sólo DESPUÉS avisa a la pantalla, de modo que un parámetro llamado
 * `__ob__` ya estaba pisado por el `Observer` cuando `tomarEstadoDe` recorría el mapa: la fila no
 * se enseñaba y el guardado siguiente la borraba. Reconstruido aquí, el mapa llega congelado, Vue
 * no lo observa y la fila se ve y se guarda como cualquier otra.
 */
CronTask.conParametrosBlindados = function(documento) {
    if (!documento || typeof documento !== 'object') return documento;
    const origen = documento.datos;
    if (!origen || typeof origen !== 'object') return documento;
    documento.datos = CronTask.mapaDeParametros(Object.keys(origen).map(function(clave) {
        return [clave, origen[clave]];
    }));
    return documento;
};

// Las TRES puertas por las que un documento con parámetros entra en la ficha. `getList` no pasa por
// aquí porque la lista ni enseña ni reescribe `datos` —solo el nombre, el cron y el estado—.
//
// `execute` sí pasa desde que su respuesta es la tarea guardada y la ficha la pinta: antes solo se
// leía de ella `resultadoUltimaEjecucion` y no hacía falta blindar nada. Es el mismo olvido de las
// otras dos, en la puerta que acaba de abrirse.
CronTask.get = function(params, forceReload) {
    return DatabaseModel.get.call(this, params, forceReload).then(CronTask.conParametrosBlindados);
};

CronTask.save = function(params) {
    return DatabaseModel.save.call(this, params).then(CronTask.conParametrosBlindados);
};

/**
 * Lanza la tarea AHORA y devuelve la tarea <b>como ha quedado guardada</b>: con el resultado, la
 * fecha de esta ejecución, las observaciones y la próxima ejecución calculada.
 *
 * Va contra `POST /movilidad/tareasProgramadas/{id}/ejecutar/`, de movilidad, y no contra el
 * `GET /core/schedulerTasks/{id}/execute/` que había: ese ejecuta la tarea pero NO guarda nada
 * —rellena esos campos en la respuesta y los tira—, así que la ficha se quedaba en blanco después
 * de una ejecución que sí había corrido y nadie podía ver desde la pantalla si terminó bien
 * (dirección, 16 y 17/10/2026). El del core sigue existiendo y no se ha tocado; lo que cambia es
 * por dónde lanza la pantalla.
 *
 * Es la única puerta de lanzar que hay, y por eso se cambia aquí y no en cada pantalla: la usan el
 * botón «Lanzar ahora» de la ficha y el icono de ejecutar de la lista, y las dos dejan constancia.
 */
CronTask.execute = function(params) {
    var self = this;
    params = params || {};
    if (!params.itemId) return Promise.reject({ error: 'itemId not defined' });

    var requestParams = {
        url: self.server_movilidad + self.prefix + 'tareasProgramadas/' + params.itemId + '/ejecutar/',
        method: 'POST',
        data: {}
    };
    return self.sendRequest(requestParams, false).then(CronTask.conParametrosBlindados);
};

// Cómo acabó una ejecución lanzada a mano. La respuesta trae `resultadoUltimaEjecucion`, así que el
// aviso puede decirlo en vez de dar por buena cualquier ejecución: un KO anunciado como «lanzada
// correctamente» es un control que miente.
CronTask.resultadoEjecucion = function(res) {
    const resultado = res?.resultadoUltimaEjecucion;
    if (resultado === 'KO') return { ok: false, clave: 'crontask.ejecucionKo' };
    if (resultado === 'OK') return { ok: true, clave: 'crontask.ejecucionOk' };
    return { ok: true, clave: 'crontask.ejecucionLanzada' };
};

export default CronTask;
